import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import HistoryIcon from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { StyledTable, TitleBar, SnackDisplay, LoadingBackDrop } from "@lp/lp-ui";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getCurrentUserData } from "../../app/currentUserSlice";
import { AuditLog, AuditLogDetails, OrganizationModel, PaginationResponse } from "../../typings";
import identityService from "../../services/IdentityService";
import { getBodyErrors } from "../../services/ApiService";
import { AuditLogDialog } from "../common/AuditLogDialog";
import OrganizationDialog from "./OrganizationDialog";
import OrganizationsDocDialog from "./OrganizationsDocDialog";

const Organizations = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((store) => store.currentUser);
  const [organizations, setOrganizations] = useState<OrganizationModel[]>([]);
  const [filterString, setFilterString] = useState<string>("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationModel | null>(null);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState<"success" | "error">("success");
  const [auditOrganization, setAuditOrganization] = useState<{ id: number; name: string } | null>(null);
  const [docOpen, setDocOpen] = useState(false);

  const auditOrganizationId = auditOrganization?.id;

  const showAuditError = (message: string) => {
    setSnackColor("error");
    setSnackMessage(message);
  };

  const loadAuditLogs = useCallback(async (): Promise<PaginationResponse<AuditLog> | null> => {
    if (!auditOrganizationId) return null;
    try {
      const response = await identityService.getOrganizationAuditLogs(auditOrganizationId);
      return response.data;
    } catch {
      showAuditError("Failed to load audit logs.");
      return null;
    }
  }, [auditOrganizationId]);

  const loadAuditLogDetails = useCallback(
    async (auditLogId: number): Promise<AuditLogDetails | null> => {
      if (!auditOrganizationId) return null;
      try {
        const response = await identityService.getOrganizationAuditLogDetails(auditOrganizationId, auditLogId);
        return response.data;
      } catch {
        showAuditError("Failed to load audit log details.");
        return null;
      }
    },
    [auditOrganizationId]
  );

  const isLoadingCurrentUser = currentUser.data === undefined;
  const isTenantAdmin = currentUser.data?.isTenantAdmin === true;

  const loadOrganizations = async () => {
    try {
      const response = await identityService.getOrganizations();
      setOrganizations(response.data);
    } catch (err: any) {
      const bodyErrors = await getBodyErrors(err);
      setSnackColor("error");
      setSnackMessage(bodyErrors[0]?.detail ?? "Failed to load organizations.");
    }
  };

  useEffect(() => {
    if (isTenantAdmin) {
      loadOrganizations();
    }
  }, [isTenantAdmin]);

  // Wait for the current user to load before deciding access — otherwise the
  // first render (data not yet populated) would bounce a legitimate TenantAdmin.
  if (isLoadingCurrentUser) {
    return <LoadingBackDrop open />;
  }

  // Defense in depth: the nav link is already hidden for non-tenant-admins.
  if (!isTenantAdmin) {
    return <Navigate to="/" replace />;
  }

  const openCreate = () => {
    setSelectedOrganization(null);
    setDialogOpen(true);
  };

  const openEdit = (organization: OrganizationModel) => {
    setSelectedOrganization(organization);
    setDialogOpen(true);
  };

  const handleDialogClose = (savedMessage?: string, color: "success" | "error" = "success") => {
    setDialogOpen(false);
    setSelectedOrganization(null);
    if (savedMessage) {
      loadOrganizations();
      // Refresh currentUser so other screens' org lists (currentUser.data.organizations) reflect the change.
      dispatch(getCurrentUserData());
      setSnackColor(color);
      setSnackMessage(savedMessage);
    }
  };

  const filtered = organizations
    .filter(
      (o) =>
        o.name.toLowerCase().includes(filterString.toLowerCase()) ||
        (o.displayName ?? "").toLowerCase().includes(filterString.toLowerCase())
    )
    .sort((a, b) => (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name));

  return (
    <React.Fragment>
      <TitleBar
        title="Organizations"
        subtitle={`${organizations.length} records found`}
        actionComponents={[
          // TitleBar drops actionComponents into a plain Box, so the flex wrapper here
          // is what keeps the button and the info icon from sitting flush together.
          <Box key="organizations-actions" display="flex" gap={1} alignItems="center">
            <Button variant="outlined" onClick={openCreate}>
              New Organization
            </Button>
            <Tooltip title="Organizations documentation">
              <IconButton
                size="small"
                aria-label="Organizations documentation"
                onClick={() => setDocOpen(true)}
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>,
        ]}
      />
      <Paper>
        <Grid container spacing={1} m={1}>
          <Grid item xs={4}>
            <TextField
              placeholder="Quick Search"
              variant="outlined"
              size="small"
              value={filterString}
              fullWidth
              autoComplete="off"
              onChange={(e) => setFilterString(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton>
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <StyledTable stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell align="right">Audit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((organization) => (
              <TableRow
                hover
                key={organization.id}
                sx={{ cursor: "pointer" }}
                onClick={() => openEdit(organization)}
              >
                <TableCell>{organization.name}</TableCell>
                <TableCell>{organization.displayName}</TableCell>
                <TableCell align="right">
                  <Tooltip title="View audit logs">
                    <IconButton
                      size="small"
                      aria-label={`View audit logs for ${organization.displayName ?? organization.name}`}
                      // The row opens the edit dialog, so keep the click from reaching it.
                      onClick={(e) => {
                        e.stopPropagation();
                        setAuditOrganization({
                          id: organization.id,
                          name: organization.displayName ?? organization.name,
                        });
                      }}
                    >
                      <HistoryIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </Paper>

      <OrganizationDialog
        open={dialogOpen}
        organization={selectedOrganization}
        handleClose={handleDialogClose}
      />

      <OrganizationsDocDialog open={docOpen} handleClose={() => setDocOpen(false)} />

      <AuditLogDialog
        open={auditOrganization !== null}
        subject={auditOrganization?.name}
        handleClose={() => setAuditOrganization(null)}
        loadAll={loadAuditLogs}
        loadDetails={loadAuditLogDetails}
      />

      {snackMessage && (
        <SnackDisplay snack={snackMessage} color={snackColor} handleClose={() => setSnackMessage("")} />
      )}
    </React.Fragment>
  );
};

export default Organizations;
